/** Offset + size of content with `object-fit: contain` in a WxH box. */
export function getObjectFitContainRect(containerW, containerH, contentW, contentH) {
  if (!(containerW > 0) || !(containerH > 0) || !(contentW > 0) || !(contentH > 0)) {
    return { ox: 0, oy: 0, rw: Math.max(0, containerW), rh: Math.max(0, containerH) };
  }
  const scale = Math.min(containerW / contentW, containerH / contentH);
  const rw = contentW * scale;
  const rh = contentH * scale;
  const ox = (containerW - rw) / 2;
  const oy = (containerH - rh) / 2;
  return { ox, oy, rw, rh };
}

/**
 * Map a viewport point to image pixel coordinates inside an object-fit contain box.
 * @returns {{ x: number, y: number } | null}
 */
export function clientPointToImageCoords(clientX, clientY, containerEl, logicalW, logicalH) {
  if (!containerEl || !(logicalW > 0) || !(logicalH > 0)) return null;
  const rect = containerEl.getBoundingClientRect();
  const { ox, oy, rw, rh } = getObjectFitContainRect(rect.width, rect.height, logicalW, logicalH);
  const localX = clientX - rect.left - ox;
  const localY = clientY - rect.top - oy;
  if (localX < 0 || localY < 0 || localX > rw || localY > rh) return null;
  return {
    x: (localX / rw) * logicalW,
    y: (localY / rh) * logicalH,
  };
}

export const CURATED_PLACEMENT_WIDTH_RATIO = 0.12;
export const CURATED_PLACEMENT_MIN_LOGICAL_PX = 48;

/** Default placement width in image pixel space (matches canvas drop). */
export function defaultCuratedPlacementLogicalWidth(logicalW) {
  if (!(logicalW > 0)) return CURATED_PLACEMENT_MIN_LOGICAL_PX;
  return Math.max(
    CURATED_PLACEMENT_MIN_LOGICAL_PX,
    logicalW * CURATED_PLACEMENT_WIDTH_RATIO,
  );
}

/** Screen-pixel width of a new placement so the drag ghost matches on-canvas size. */
export function defaultCuratedPlacementScreenWidth(
  containerW,
  containerH,
  logicalW,
  logicalH,
) {
  if (!(containerW > 0) || !(containerH > 0) || !(logicalW > 0) || !(logicalH > 0)) {
    return 72;
  }
  const placementLogicalW = defaultCuratedPlacementLogicalWidth(logicalW);
  const { rw } = getObjectFitContainRect(containerW, containerH, logicalW, logicalH);
  return (placementLogicalW / logicalW) * rw;
}

/** Image pixel coords → % position within the contain box (for overlay layout). */
export function imageCoordsToContainPercent(x, y, logicalW, logicalH) {
  if (!(logicalW > 0) || !(logicalH > 0)) return { leftPct: 50, topPct: 50 };
  return {
    leftPct: (x / logicalW) * 100,
    topPct: (y / logicalH) * 100,
  };
}
